/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import ChartConst from '../../../constants/chart.constant';
import DefConst from '../../../constants/def.constant';
import AppConst from '../../../constants/app.constant';
import bnd3Service from '../../../services/bnd3.service';
import utilityService from '../../../services/utility.service';
import { is_iphone } from '../../../services/utils/is_iphone';
import { is_ipad } from '../../../services/utils/is_ipad';
import bnChartDataProcessService from '../../../services/chartDataProcess.service';
import d3 from '../../../globalexports/d3.export';
import { validateAloftData } from '../../../services/validation/validateAloftData';

function trackLineService() {
  const content = {};
  let trendsForHide;
  let barbForHide;
  content.drawTrackLine = function (
    majorContainerHeight,
    xAxisTopContainerHeight,
    xAxisBottomContainerHeight,
    hideBottomAxis,
    settings,
    chartHeight,
    ceilingChartHeight,
    width,
    allCharts,
    dateFormat,
    sourceData,
    topSpace,
    margin,
    ceilingChartY,
    rulesetStripHeight,
    flightWeatherStripHeight,
    yAxisRectHeight,
    yAxisRectWidth,
    yAxisRightRectWidth,
    aloftData,
    ceilingChartAxis,
    trendSvg,
    ceilingMaxValueAllowed,
    ceilingMinValueAllowed,
  ) {
    content.detachtrendlineEvents();
    // var altitudeContainer;
    // var yposMain = {};
    const topMarginFactor = flightWeatherStripHeight + rulesetStripHeight;
    const tooltipRadius = 3;
    const rectRadius = 2;
    const elevationHeight = Math.max(ceilingChartHeight - allCharts.ceilingChart.axisInfo.y1(
      settings.airportDetails.el,
    ), 1);
    const viewPrefix = 'meteo-';
    const normalTextHeight = bnd3Service.getSelectionHeight(d3.select('#bntextrefmeteo'));
    const ceilingMinValueYValuePossible = allCharts.ceilingChart.axisInfo.y1(
      ceilingMinValueAllowed,
    );
    const ceilingMaxValueYValuePossible = allCharts.ceilingChart.axisInfo.y1(
      ceilingMaxValueAllowed,
    );
    const flightDots = is_iphone()
      ? ChartConst.flightDotsMobile
      : ChartConst.flightDots;

    // Combining all chart data to single object to render trend line
    const dataCollection = utilityService.clone(allCharts.ceilingChart.data);
    let altitudeData = DefConst[`aloftAltitudeMap${AppConst.dataMode.meteogram}`];
    // console.log(allCharts.precepitationChart.data);
    let temp = [];
    _forEach(altitudeData, (item) => {
      if (item >= ceilingMinValueAllowed) {
        temp.push(item);
      }
    });
    altitudeData = temp;
    if (!hideBottomAxis) {
      _forEach(allCharts.precepitationChart.data, (data) => {
        dataCollection.push(data);
      });
      _forEach(allCharts.windChart.data, (data) => {
        dataCollection.push(data);
      });
      _forEach(allCharts.tempChart.data, (data) => {
        dataCollection.push(data);
      });
    }
    const barbOpacity = ChartConst.aloftBarbOpacityShowBarb;
    let defaultWindSpeed;
    if (is_iphone()) {
      defaultWindSpeed = ChartConst.windSpeed.mobileLight;
    } else if (is_ipad()) {
      defaultWindSpeed = ChartConst.windSpeed.tabletLight;
    } else {
      defaultWindSpeed = ChartConst.windSpeed.light;
    }
    const trackLineLength = majorContainerHeight - xAxisTopContainerHeight
      - xAxisBottomContainerHeight - (topSpace / 2);

    const trendLineContainer = trendSvg.append('g')
      .attr('id', 'meteo-trendLineContainer')
      .attr('transform', () => {
        const x = margin.left;
        const y = 0;
        return `translate(${x}, ${y})`;
      });
    let elevationContainer;

    function drawElevation() {
      elevationContainer.append('rect')
        .attr('width', () => width - yAxisRectWidth - yAxisRightRectWidth)
        .attr('height', () => elevationHeight)
        .attr('transform', () => {
          const yValue = ceilingChartHeight - elevationHeight;
          return `translate(${yAxisRectWidth}, ${yValue})`;
        })
        .attr('class', 'area')
        .attr('fill', ChartConst.ChartLabelSpecs.elv.color);
    }
    let aloftWindBarbsContainer;

    function addAltitudeComponent() {
      if (validateAloftData(aloftData)) {
        aloftWindBarbsContainer = trendLineContainer.append('g')
          .attr('id', 'meteo-barbs-container')
          .attr('class', 'aloftWindBarbsContainer')
          .attr('barbOpacity', barbOpacity)
          .attr('transform', `translate(0, ${xAxisTopContainerHeight + topMarginFactor})`);
        // Creating marker for barbhead
        bnd3Service.addBarbMarker(
          aloftWindBarbsContainer,
          'meteo-barbheadLight-aloft',
          defaultWindSpeed,
          true,
        );
        bnd3Service.drawAloftWindBarbs(
          ceilingMinValueAllowed,
          settings.maximumAltitudeAllowed,
          aloftData.exactMatchData,
          altitudeData,
          defaultWindSpeed,
          aloftWindBarbsContainer,
          ceilingChartAxis.x,
          ceilingChartAxis.y1,
          'x',
          viewPrefix,
          null,
          settings,
        );
        // Drawing clear rect after ceiling chart to hide leaking barbs
        trendLineContainer.append('rect')
          .attr('class', 'clear-rect')
          .attr('x', yAxisRectWidth)
          .attr('y', xAxisTopContainerHeight + flightWeatherStripHeight + rulesetStripHeight
            + ceilingChartHeight)
          .attr('width', width - yAxisRectWidth + 2)
          .attr('height', topSpace);
      }
      elevationContainer = trendLineContainer.append('g')
        .attr('id', 'meteo-elevation')
        .attr(
          'transform',
          `translate(0, ${xAxisTopContainerHeight + flightWeatherStripHeight + rulesetStripHeight})`,
        );
      if (validateAloftData(aloftData)) {
        drawElevation();
      }
      // This will be horizontal altitude line
      const horizontalAltitudeLine = trendLineContainer.append('g')
        .attr('class', 'horizontal-altitude-line-meteo part-of-tooltip')
        .attr('id', 'meteohorizontalAltitudeLine')
        .style('display', 'none');
      horizontalAltitudeLine.append('line')
        .attr('id', 'horizontal-altitude-line-meteo')
        .attr('x1', yAxisRectWidth)
        .attr('y1', 0)
        .attr('x2', width - yAxisRightRectWidth)
        .attr('y2', 0);
      const horizontalAltitudeTooltipContainer = horizontalAltitudeLine.append('g')
        .attr('id', 'meteohorizontalAltitudeTooltipContainer')
        .attr('class', 'horizontal-altitude-line-meteo-tooltip');
      horizontalAltitudeTooltipContainer.append('rect')
        .attr('height', normalTextHeight)
        .attr('rx', rectRadius)
        .attr('ry', rectRadius);
      horizontalAltitudeTooltipContainer.append('text')
        .style('text-anchor', 'start')
        .style('fill', '#fff');
      trendLineContainer.append('g')
        .attr('class', 'mouse-move-tracker part-of-tooltip')
        .attr('id', 'meteoaltitudeContainer')
        .style('display', 'none');
    }
    if (validateAloftData(aloftData)) {
      addAltitudeComponent();
    }
    const trends = trendLineContainer.selectAll('group')
      .data(allCharts.ceilingChart.data[0].values)
      .enter();
    const trend = trends.append('g')
      .attr('class', 'meteo-trend-line part-of-tooltip')
      .attr('transform', (d) => `translate(${allCharts.ceilingChart.axisInfo.x(d.x)}, ${xAxisTopContainerHeight})`)
      .attr('id', (...args) => {
        const key = args[1];
        return `meteo-trend-${key}`;
      })
      .attr('valueindex', (...args) => {
        const key = args[1];
        return key;
      })
      .attr('tooltipWestGravity', (d) => !!(allCharts.ceilingChart.axisInfo.x(d.x) > (window
        .innerWidth / 2)));
    if (utilityService.isFirefox()) {
      trend.style('opacity', '0');
    } else {
      trend.style('display', 'none');
    }
    const tooltipPositionFactor = 4;
    const timeHourTopTooltip = trend.append('g')
      .attr('class', 'textTooltips timeHourTopTooltip');
    const timeHourBottomTooltip = trend.append('g')
      .attr('class', 'textTooltips timeHourBottomTooltip')
      .attr(
        'transform',
        `translate(0, ${trackLineLength + normalTextHeight + (topSpace / 2) + (tooltipPositionFactor / 2)})`,
      );

    timeHourTopTooltip.append('rect')
      .attr('rx', tooltipRadius)
      .attr('ry', tooltipRadius);
    timeHourTopTooltip.append('text')
      .text((d) => dateFormat(d.x))
      .attr('x', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        return -(bnd3Service.getSelectionWidth(d3.select(selfEle)) / 2);
      })
      .attr('y', -tooltipPositionFactor);

    timeHourTopTooltip.select('rect')
      .attr('width', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        return bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select('text'))
          + tooltipPositionFactor;
      })
      .attr('height', normalTextHeight + 2)
      .attr('transform', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        const x = bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select('text'))
          + tooltipPositionFactor;
        const y = -(normalTextHeight + (tooltipPositionFactor / 2));
        return `translate(${-(x / 2)},${y})`;
      });
    if (!hideBottomAxis) {
      timeHourBottomTooltip.append('rect')
        .attr('rx', tooltipRadius)
        .attr('ry', tooltipRadius);
      timeHourBottomTooltip.append('text')
        .text((d) => dateFormat(d.x))
        .attr('x', (...args) => {
          const selfEle = utilityService.getSelfElement(args);
          return -(bnd3Service.getSelectionWidth(d3.select(selfEle)) / 2);
        })
        .attr('y', -tooltipPositionFactor);

      timeHourBottomTooltip.select('rect')
        .attr('width', (...args) => {
          const selfEle = utilityService.getSelfElement(args);
          return bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select('text'))
            + tooltipPositionFactor;
        })
        .attr('height', normalTextHeight + 2)
        .attr('transform', (...args) => {
          const selfEle = utilityService.getSelfElement(args);
          const x = bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select(
            'text',
          )) + tooltipPositionFactor;
          const y = -(normalTextHeight + (tooltipPositionFactor / 2));
          return `translate(${-(x / 2)},${y})`;
        });
    }
    const flightRuleTooltips = trend.append('g')
      .attr('class', 'flightRuleTooltips');
    flightRuleTooltips.append('rect')
      .attr('fill', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(
          d3.select(selfEle.parentNode.parentNode).attr('valueindex'),
          10,
        );
        if (sourceData.forecast[valueIndex]) {
          return bnd3Service.getFlightDotColorForRruleset(sourceData.forecast[valueIndex]
            .frlServerValue || sourceData.forecast[valueIndex].frl);
        }
      });
    flightRuleTooltips.append('text')
      .style('stroke', '#fff')
      .style('stroke-width', '0px')
      .attr('x', 2)
      .style('fill', '#fff')
      .text((...args) => {
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(
          d3.select(selfEle.parentNode.parentNode).attr('valueindex'),
          10,
        );
        if (sourceData.forecast[valueIndex]) {
          return `${bnd3Service.getFlightDotTextForRruleset(sourceData.forecast[valueIndex].frlServerValue || sourceData.forecast[valueIndex].frl)}, ${utilityService.getCloudText(sourceData.forecast[valueIndex].cld) || '--'}`;
        }
      });

    flightRuleTooltips.select('rect')
      .attr('width', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        return bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select('text')) + 4;
      })
      .attr('height', normalTextHeight);
    flightRuleTooltips.select('text').attr('y', (...args) => {
      const selfEle = utilityService.getSelfElement(args);
      const tooltipTextHeight = parseFloat(d3.select(selfEle.parentNode).select('text').style(
        'font-size',
      ));
      return tooltipTextHeight - 1;
    });
    flightRuleTooltips.attr('transform', (...args) => {
      const selfEle = utilityService.getSelfElement(args);
      let x = (flightDots.radius + flightDots.strokeWidth);
      const y = (flightWeatherStripHeight + ((rulesetStripHeight - normalTextHeight) / 2));
      const tooltipWestGravity = !!(d3.select(selfEle.parentNode).attr(
        'tooltipWestGravity',
      ) === 'true');
      if (tooltipWestGravity) {
        x -= bnd3Service.getSelectionWidth(d3.select(selfEle).select('text')) + 4 + (
          flightDots.radius * 2) + (flightDots.strokeWidth * 2);
      }
      return `translate(${x}, ${y})`;
    });
    trend.append('line')
      .attr('class', 'path-trend-line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', topMarginFactor)
      .attr('y2', trackLineLength);
    const trendPerLine = trend.selectAll('.trend-per-line-meteo')
      .data(dataCollection)
      .enter()
      .append('g')
      .attr('class', (...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(d3.select(selfEle.parentNode).attr('valueindex'), 10);
        return `trend-per-line-meteo trend-per-line-meteo-${d.group}${d.values[valueIndex].hide
          ? '-hide'
          : ''}`;
      })
      .style('opacity', (...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(d3.select(selfEle.parentNode).attr('valueindex'), 10);
        return (d.secondary && d.values[valueIndex].hide)
          ? 0
          : 1;
      })
      .attr('data-value', (...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(d3.select(selfEle.parentNode).attr('valueindex'), 10);
        return `${d.key}-${d.values[valueIndex]
          ? d.values[valueIndex].y
          : null}`;
      });
    trendPerLine.append('circle')
      .attr('class', 'line-value-circle')
      .attr('r', ChartConst.trendLineTooltipsCircleRadius)
      .attr('isAreaSeries', (d) => d.area)
      .attr('seriesKey', (d) => d.key)
      .attr('seriesValue', (...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(
          d3.select(selfEle.parentNode.parentNode).attr('valueindex'),
          10,
        );
        return (d.values[valueIndex]
          ? d.values[valueIndex].y
          : null);
      })
      .style('stroke', (d) => d.color);

    const trendLineTooltips = trendPerLine.append('g')
      .attr('class', 'trend-tooltip-container-meteo')
      .attr('id', (...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(
          d3.select(selfEle.parentNode.parentNode).attr('valueindex'),
          10,
        );
        return `trend-tooltip-container-meteo-${valueIndex}${d.values[valueIndex].hide
          ? '-hide'
          : ''}`;
      });

    trendLineTooltips.append('rect')
      .attr('class', 'tooltip-value-rect')
      .attr('transform', 'translate(5, -10)');
    const textNode = trendLineTooltips.append('text')
      .text((...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(d3.select(selfEle.parentNode.parentNode.parentNode).attr(
          'valueindex',
        ), 10);
        const dMain = d.values[valueIndex];
        const tooltipParts = dMain
          ? bnChartDataProcessService.formatTooltipText(d, dMain, settings)
          : '';
        if (tooltipParts === 'string') {
          return tooltipParts;
        }
        return `${tooltipParts[0]}:`;
      })
      .style('fill', (d) => d.color);
    textNode.append('tspan').attr('class', 'boldtext').text((...args) => {
      const d = args[0];
      const selfEle = utilityService.getSelfElement(args);
      const valueIndex = parseInt(d3.select(selfEle.parentNode.parentNode.parentNode
        .parentNode).attr('valueindex'), 10);
      const dMain = d.values[valueIndex];
      if (dMain) {
        const tooltipParts = dMain
          ? bnChartDataProcessService.formatTooltipText(d, dMain, settings)
          : '';
        if (tooltipParts[1]) {
          return tooltipParts[1];
        }
      }
    });
    trendLineTooltips.select('rect')
      .attr('width', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        return bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select('text')) + 4;
      })
      .attr('height', normalTextHeight);
    if (validateAloftData(aloftData)) {
      // Drawing AloftBarbs tooltips
      const aloftWindBarbsTooltipContainer = trendLineContainer.append('g')
        .attr('id', 'meteo-barbs-tooltip-container')
        .attr('class', 'aloftWindBarbsTooltipContainer')
        .attr('barbOpacity', barbOpacity)
        .attr('transform', `translate(0, ${xAxisTopContainerHeight + topMarginFactor})`);
      bnd3Service.drawAloftWindBarbsTooltips(
        ceilingMinValueAllowed,
        settings.maximumAltitudeAllowed,
        aloftData.exactMatchData,
        altitudeData,
        aloftWindBarbsTooltipContainer,
        ceilingChartAxis.x,
        ceilingChartAxis.y1,
        'x',
        viewPrefix,
        flightDots,
        defaultWindSpeed,
        null,
        settings,
        normalTextHeight,
      );
    }
    const returnObj = [];
    returnObj.push(
      chartHeight,
      ceilingChartHeight,
      allCharts,
      topSpace,
      ceilingMinValueAllowed,
      trendPerLine,
      topMarginFactor,
      ceilingMinValueYValuePossible,
      ceilingMaxValueYValuePossible,
      elevationHeight,
      trendLineTooltips,
    );
    // eslint-disable-next-line prefer-rest-params
    content.positionTooltips.apply(utilityService.getSelfElement(arguments), returnObj);

    function hideAllTrendLines() {
      if (!trendsForHide) {
        trendsForHide = d3.selectAll('.meteo-trend-line');
      }
      if (!barbForHide) {
        barbForHide = d3.selectAll('.meteo-aloft-barb');
      }
      trendsForHide.style('display', 'none');
      if (barbForHide) {
        barbForHide.style('display', 'none');
      }
      d3.select('#meteoaltitudeContainer').style('display', 'none');
      d3.select('#meteohorizontalAltitudeLine').style('display', 'none');
    }

    function trendLineWorker(mouse, d3Event) {
      const trackLineContainer = document.getElementById('trendLineTrackareaMeteo');
      if (trackLineContainer) {
        // var mouse = d3.pointer(trackLineContainer);
        // This will handle notch
        mouse[0] -= margin.left;
        let yAltitude = allCharts.ceilingChart.axisInfo.y1.invert((mouse[1]) - (ceilingChartY));

        const xDate = allCharts.ceilingChart.axisInfo.x.invert(mouse[0]);
        const bisect = d3.bisector((d) => d.x).right;
        const altitudeBisect = d3.bisector((d) => d).right;
        let altitudeMain;
        let nearestIdx;
        let dMain;
        let altitudeNearestIdx;
        // idx storing data index closest to mouse position
        if (altitudeData && altitudeData[0] < settings.airportDetails.el && yAltitude
          < altitudeData[1]) {
          // this will start alt tooltip after elevation rectangle
          yAltitude = altitudeData[1] - 1;
        }
        const idx = bisect(allCharts.ceilingChart.data[0].values, xDate);
        altitudeData = DefConst[`aloftAltitudeMap${AppConst.dataMode.meteogram}`];
        temp = [];
        _forEach(altitudeData, (item) => {
          if (item >= ceilingMinValueAllowed) {
            temp.push(item);
          }
        });
        altitudeData = temp;
        const altitudeIdx = altitudeBisect(altitudeData, yAltitude);
        // hide all the trendlines
        hideAllTrendLines();
        const d0 = allCharts.ceilingChart.data[0].values[idx - 1]; // Nearest value at left
        const d1 = allCharts.ceilingChart.data[0].values[idx]; // Nearest value at right
        const altitudeD0 = altitudeData[altitudeIdx - 1]; // Nearest value at left
        const altitudeD1 = altitudeData[altitudeIdx]; // Nearest value at right
        if (d0 || d1) {
          if (d0 && d1) {
            if (xDate - d0.x > d1.x - xDate) {
              nearestIdx = idx;
              dMain = d1;
            } else {
              nearestIdx = idx - 1;
              dMain = d0;
            }
          } else {
            dMain = d0 || d1;
            if (d0) {
              nearestIdx = idx - 1;
            } else {
              nearestIdx = idx;
            }
          }
          if (altitudeD0 >= 0 && altitudeD1 >= 0) {
            if (yAltitude - altitudeD0 > altitudeD1 - yAltitude) {
              altitudeNearestIdx = altitudeIdx;
              altitudeMain = altitudeD1;
              if ((allCharts.ceilingChart.axisInfo.y1(altitudeMain) + xAxisTopContainerHeight
                  + topMarginFactor) < (xAxisTopContainerHeight + topMarginFactor)) {
                altitudeMain = altitudeD0;
                altitudeNearestIdx = altitudeIdx - 1;
              }
            } else {
              altitudeNearestIdx = altitudeIdx - 1;
              altitudeMain = altitudeD0;
            }
          } else {
            altitudeMain = altitudeD0 || altitudeD1;
            if (altitudeD0) {
              altitudeNearestIdx = altitudeIdx - 1;
            } else {
              altitudeNearestIdx = altitudeIdx;
            }
          }
        }
        // Finding altitude value at mouse Y coordinates
        // ToDO : find a way to limit mouse[1] i.e.y cordinate value for only ceiling chart
        const altitude = allCharts.ceilingChart.axisInfo.y1.invert(mouse[1] - ceilingChartY)
          .toFixed(0);
        if (altitude >= ceilingMinValueAllowed && altitude <= ceilingMaxValueAllowed) {
          // Doing math.min to make sure that 6k and 12K ft altitude get correct mid points
          // altitudeMain = altitudeMain;
          // If altitude value is under ceiling max/min domian
          // console.log(altitudeNearestIdx, nearestIdx);
          d3.selectAll(`.meteo-aloft-barb-group-${altitudeNearestIdx}`).style(
            'display',
            'inherit',
          );
          d3.selectAll(`.meteo-aloft-barb-group-${altitudeNearestIdx}-${nearestIdx}`).style('display', 'inherit');
          d3.select('#meteoaltitudeContainer').style('display', 'inherit');
          d3.select('#meteohorizontalAltitudeLine').style('display', 'inherit');
          const horizontalAltitudeTooltipContainer = d3.select(
            '#meteohorizontalAltitudeTooltipContainer',
          );
          horizontalAltitudeTooltipContainer.select('text').text(() => utilityService
            .abbreviateAltitudeCeiling(parseInt(altitudeMain, 10), ChartConst.ChartLabelSpecs
              .cig.tooltip.mobileUnit));
          horizontalAltitudeTooltipContainer.select('rect').attr('width', (...args) => {
            const selfEle = utilityService.getSelfElement(args);
            return bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select(
              'text',
            )) + 3;
          });
          horizontalAltitudeTooltipContainer.select('rect')
            .attr('transform', (...args) => {
              const selfEle = utilityService.getSelfElement(args);
              const y = (-(normalTextHeight / 2));
              const barbPadding = utilityService.getBarbPadding();
              const rectWidth = bnd3Service.getSelectionWidth(d3.select(selfEle));
              const x = yAxisRectWidth - rectWidth - barbPadding;
              return `translate(${x}, ${y})`;
            });
          horizontalAltitudeTooltipContainer.select('text')
            .attr('y', '.35em')
            .attr('x', (...args) => {
              const selfEle = utilityService.getSelfElement(args);
              const barbPadding = utilityService.getBarbPadding();
              const rectWidth = bnd3Service.getSelectionWidth(d3.select(selfEle));
              return yAxisRectWidth - rectWidth - barbPadding - 1.5;
            });
          // console.log(altitudeMain, (allCharts.ceilingChart.axisInfo.y1(altitudeMain)));
          d3.select('#meteoaltitudeContainer').attr(
            'transform',
            `translate(${allCharts.ceilingChart.axisInfo.x(dMain.x)},${allCharts.ceilingChart.axisInfo.y1(altitudeMain) + xAxisTopContainerHeight + topMarginFactor})`,
          );
          d3.select('#meteohorizontalAltitudeLine').attr(
            'transform',
            `translate(0 ,${allCharts.ceilingChart.axisInfo.y1(altitudeMain) + xAxisTopContainerHeight + topMarginFactor})`,
          );
        } else {
          d3.select('#meteoaltitudeContainer').style('display', 'none');
          d3.select('#meteohorizontalAltitudeLine').style('display', 'none');
        }
        d3.select(`#meteo-trend-${nearestIdx}`).style('display', 'inherit');
        if (d3Event) {
          d3Event.stopPropagation();
        } else if (window.event) {
          // eslint-disable-next-line no-restricted-globals
          event.stopPropagation();
        }
      }
    }

    function resetAloftBarbsOpacity() {
      d3.selectAll('.meteo-aloft-barb').style('opacity', '1')
        .style('display', 'none');
      // barb-path
      d3.selectAll('.meteo-barb-path')
        .style('fill', defaultWindSpeed.color)
        .style('stroke', defaultWindSpeed.color);
      d3.select('#meteo-barbheadLight-aloft-polyline').attr('fill', defaultWindSpeed.color);
    }

    function svgToCanvas() {
      const canvasWidth = width;
      const canvasHeight = ceilingChartHeight + topMarginFactor + xAxisTopContainerHeight;
      // sourceSvgId, canvasContainerId, canvasId, canvasWidth, canvasHeight, callback
      bnd3Service.convertSvgToCanvas(
        'meteo-barbs-container',
        'aloft-canvas-container-meteo',
        'aloftBarbsCanvas-meteo',
        canvasWidth,
        canvasHeight,
        resetAloftBarbsOpacity,
      );
      bnd3Service.aloftSuppress();
    }
    svgToCanvas();
    const trendLineTrackareaMeteo = trendSvg.append('rect')
      .attr('id', 'trendLineTrackareaMeteo')
      .attr('class', 'track-area')
      .attr('width', width - (yAxisRectWidth + yAxisRightRectWidth))
      .attr('height', yAxisRectHeight - (xAxisTopContainerHeight / 2) - (topSpace / 2))
      .attr('x', yAxisRectWidth + margin.left)
      .attr('y', xAxisTopContainerHeight + flightWeatherStripHeight + rulesetStripHeight)
      .attr('fill', 'none');
    trendLineTrackareaMeteo.on('click', (d3Event) => {
      const mouse = d3.pointer(d3Event, document.getElementById('trendLineTrackareaMeteo'));
      window.requestAnimationFrame(() => {
        trendLineWorker(mouse, d3Event);
      });
    }).on('mousemove', (d3Event) => {
      const mouse = d3.pointer(d3Event, document.getElementById('trendLineTrackareaMeteo'));
      window.requestAnimationFrame(() => {
        trendLineWorker(mouse, d3Event);
      });
    }).on('touchmove', (d3Event) => {
      const mouse = d3.pointer(d3Event.touches[0], document.getElementById('trendLineTrackareaMeteo'));
      window.requestAnimationFrame(() => {
        trendLineWorker(mouse, d3Event);
      });
    });
    if (!utilityService.isCordovaApplication()) {
      trendLineTrackareaMeteo.on('mouseout', () => {
        // Hide all trendlines when mouse out
        hideAllTrendLines();
      });
    }
    if (utilityService.isFirefox()) {
      trend.style('display', 'none');
      trend.style('opacity', '1');
    }
    return returnObj;
  };
  content.positionTooltips = function (
    chartHeight,
    ceilingChartHeight,
    allCharts,
    topSpace,
    ceilingMinValueAllowed,
    trendPerLine,
    topMarginFactor,
    ceilingMinValueYValuePossible,
    ceilingMaxValueYValuePossible,
    elevationHeight,
  ) {
    const yposMain = {};
    // var suppressedMet = utilityService.getSuppressedMetFromLocalStorage();
    trendPerLine.attr('transform', (...args) => {
      const d = args[0];
      const i = args[1];
      const selfEle = utilityService.getSelfElement(args);
      const valueIndex = parseInt(d3.select(selfEle.parentNode).attr('valueindex'), 10);
      const tooltipWestGravity = !!(d3.select(selfEle.parentNode).attr(
        'tooltipWestGravity',
      ) === 'true');
      let chartPositionFactor = topMarginFactor;
      const dMain = d.values[valueIndex];
      const yPosObj = {};
      let position;
      if (!dMain) {
        return 'translate(0, -100)';
      }
      // if (suppressedMet.indexOf(d.hazard_type_identifier) > -1) {
      //     dMain.hide = true;
      //     // return "trend-per-line-meteo trend-per-line-meteo-" +
      // d.group + (d.values[valueIndex].hide
      //     //     ? "-hide"
      //     //     : "");
      // }
      if (!yposMain[`#trend-tooltip-container-meteo-${valueIndex.toString()}`]) {
        yposMain[`#trend-tooltip-container-meteo-${valueIndex.toString()}`] = {
          ypos: {
            tooltipWestGravity,
            valueIndex,
            group1: [],
            group2: [],
            group3: [],
            group4: [],
          },
        };
      }
      if (d.group === 1) {
        if (d.yAxis === 1) {
          if (ceilingMinValueYValuePossible < allCharts.ceilingChart.axisInfo.y1(dMain.y)) {
            yPosObj.y = ceilingMinValueYValuePossible - ChartConst.freezingLineMinValue;
          } else if (allCharts.ceilingChart.axisInfo.y1(dMain.y)
            < ceilingMaxValueYValuePossible) {
            yPosObj.y = ceilingMaxValueYValuePossible;
          } else {
            yPosObj.y = allCharts.ceilingChart.axisInfo.y1(dMain.y);
          }
          if (yPosObj.y === Infinity) {
            yPosObj.y = ceilingChartHeight;
          }
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        } else {
          // for visiblity dMain.v has ceiling_mapped value
          const visYValue = Math.min(dMain.v, (ceilingMinValueAllowed + ChartConst
            .visibilityTillWhatAltitude));
          const visBarHeight = ceilingChartHeight - allCharts.ceilingChart.axisInfo.y1(
            visYValue,
          );
          yPosObj.y = ceilingChartHeight - elevationHeight - (visBarHeight / 2);
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        }
      }
      if (d.group === 2) {
        chartPositionFactor += ceilingChartHeight + topSpace;
        // For Precepitation chart
        if (d.yAxis === 1) {
          yPosObj.y = allCharts.precepitationChart.axisInfo.y1(dMain.y);
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        } else {
          yPosObj.y = allCharts.precepitationChart.axisInfo.y2(dMain.y);
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        }
      }

      if (d.group === 3) {
        // chartHeight * 1 because considering precip
        chartPositionFactor += ((chartHeight + ceilingChartHeight) + (topSpace * 2));
        // For Wind chart
        if (d.yAxis === 1) {
          yPosObj.y = allCharts.windChart.axisInfo.y1(dMain.y);
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        } else {
          yPosObj.y = allCharts.windChart.axisInfo.y2(dMain.y);
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        }
      }
      if (d.group === 4) {
        // chartHeight * 2 because considering precip and wind chart
        chartPositionFactor += (((chartHeight * 2) + ceilingChartHeight) + (topSpace * 3));
        // For temperature chart
        if (d.yAxis === 1) {
          // Special case of temperature to allign tooltip at bootom when null value i.e. noData
          yPosObj.y = dMain.y !== null
            ? allCharts.tempChart.axisInfo.y1(dMain.y)
            : chartHeight;
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        } else {
          yPosObj.y = allCharts.tempChart.axisInfo.y2(dMain.y);
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        }
      }
      // console.log(d);
      yPosObj.ind = i;
      yPosObj.off = 0;
      yPosObj.group = d.group;
      yPosObj.hide = dMain.hide;
      if (!yPosObj.hide) {
        yposMain[`#trend-tooltip-container-meteo-${valueIndex.toString()}`].ypos[
          `group${d.group}`].push(yPosObj);
      }
      return position;
    }).call(() => {
      let yposObj;
      let toolTipContainerObj;
      Object.keys(yposMain).forEach((yposObjKey) => {
        toolTipContainerObj = d3.selectAll(yposObjKey);
        yposObj = yposMain[yposObjKey].ypos;
        yposMain[yposObjKey].ypos.group1 = bnd3Service.processGroup2Tips(yposMain[
          yposObjKey].ypos.group1, ceilingChartHeight, toolTipContainerObj);
        yposMain[yposObjKey].ypos.group2 = bnd3Service.processGroup2Tips(yposMain[
          yposObjKey].ypos.group2, chartHeight, toolTipContainerObj);
        yposMain[yposObjKey].ypos.group3 = bnd3Service.processGroup2Tips(yposMain[
          yposObjKey].ypos.group3, chartHeight, toolTipContainerObj);
        yposMain[yposObjKey].ypos.group4 = bnd3Service.processGroupTips(yposMain[
          yposObjKey].ypos.group4, chartHeight, toolTipContainerObj);
        // Group1
        bnd3Service.alignToolTips(
          `#meteo-trend-${yposObj.valueIndex} .trend-per-line-meteo-1`,
          yposObj,
          toolTipContainerObj,
          yposObj.tooltipWestGravity,
        );
        // Group2
        bnd3Service.alignToolTips(
          `#meteo-trend-${yposObj.valueIndex} .trend-per-line-meteo-2`,
          yposObj,
          toolTipContainerObj,
          yposObj.tooltipWestGravity,
        );
        // Group3
        bnd3Service.alignToolTips(
          `#meteo-trend-${yposObj.valueIndex} .trend-per-line-meteo-3`,
          yposObj,
          toolTipContainerObj,
          yposObj.tooltipWestGravity,
        );
        // Group4
        bnd3Service.alignToolTips(
          `#meteo-trend-${yposObj.valueIndex} .trend-per-line-meteo-4`,
          yposObj,
          toolTipContainerObj,
          yposObj.tooltipWestGravity,
        );

        // Shifting aria circle points
        d3.selectAll('.line-value-circle').attr('transform', (...args) => {
          const selfEle = utilityService.getSelfElement(args);
          const x = 0;
          let y;
          if (d3.select(selfEle).attr('isAreaSeries') === 'true') {
            y = utilityService.getAdditionalShiftToHandleAreaSeries(d3.select(selfEle)
              .attr('seriesKey'));
            return `translate(${x}, ${y})`;
          }
        });
      });
    });
  };
  content.detachtrendlineEvents = function () {
    trendsForHide = null;
    barbForHide = null;
  };
  return content;
}
export default trackLineService();
